import { CardContent, styled } from '@mui/material';
import Card, { CardProps } from '@mui/material/Card';
import { styledComponent } from '../../hoc';

/**
 * `StyledCard` is a styled `Card` component used for creating consistent layouts for information cards.
 *
 * - It supports a `fullHeight` prop to set the card's height to 100% of its container.
 *
 * @example
 * ```tsx
 * <StyledCard fullHeight>
 *   <StyledCardHeader>Header Content</StyledCardHeader>
 *   <StyledCardContent>Main Content</StyledCardContent>
 * </StyledCard>
 * ```
 *
 * @category InformationCard
 */
export const StyledCard = styled(Card, {
  shouldForwardProp: (prop: string) => prop !== 'fullHeight',
})<CardProps & { fullHeight?: boolean }>(({ fullHeight }) => ({
  height: fullHeight ? '100%' : undefined,

  '&.has-overflow': {
    '& > .MuiCardContent-root': {
      paddingBottom: '48px',
    },
  },
}));

/**
 * `StyledCardHeader` is a styled `div` for arranging the header content of a card with space between elements.
 *
 * - Typically used for displaying titles and action icons.
 * - Supports theming for background color.
 *
 * @example
 * ```tsx
 * <StyledCardHeader>
 *   <h1>Title</h1>
 *   <IconButton>Action</IconButton>
 * </StyledCardHeader>
 * ```
 *
 * @category InformationCard
 */
export const StyledCardHeader = styledComponent('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 16px 8px 16px',
  width: '100%',

  backgroundColor: theme.palette.surfaceContainer.container,
}));

/**
 * `StyledCardContent` is a styled `CardContent` component that defines the main content area of a card.
 *
 * - Provides padding for proper spacing.
 * - Supports scrolling for overflowing content.
 *
 * @example
 * ```tsx
 * <StyledCardContent>
 *   <p>Content goes here</p>
 * </StyledCardContent>
 * ```
 *
 * @category InformationCard
 */
export const StyledCardContent = styled(CardContent)({
  padding: '8px 16px 16px 16px',
  height: '100%',
  overflow: 'auto',
});

/**
 * `StyledCardAction` is a styled `div` designed for action buttons or icons within a card.
 *
 * - It has a circular shape and centers its content.
 * - Includes a pointer cursor for interactive elements.
 *
 * @example
 * ```tsx
 * <StyledCardAction>
 *   <IconButton>+</IconButton>
 * </StyledCardAction>
 * ```
 *
 * @category InformationCard
 */
export const StyledCardAction = styled('div')({
  cursor: 'pointer',
  height: 52,
  width: 52,
  borderRadius: '50%',
  textAlign: 'center',
});
